// KiraFloat.jsx — draggable always-visible bubble that opens K.I.R.A
// Uses the floater-specific Friendly Bot avatar (sibling file).
import React, { useEffect, useRef, useState } from "react";
import KiraAvatar from "./KiraAvatar";

const NEON   = "#00FF99";
const SIZE   = 68;          // bubble diameter
const MARGIN = 8;           // viewport-edge keep-out (drag clamp)
const STORE  = "kira_float_pos";
const DRAG_THRESHOLD = 5;   // px of movement before a click becomes a drag
const DEFAULT_POS = { bottom: 24, right: 24 };

// Position is stored as { bottom, right } so the bubble is anchored to the
// viewport's bottom-right corner via CSS — robust to any window-size races
// at mount.  Old `{x, y}` localStorage entries are silently rejected.
function loadPos() {
  try {
    const raw = localStorage.getItem(STORE);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.bottom !== "number" || typeof p?.right !== "number") return null;
    return p;
  } catch { return null; }
}

function clamp(p) {
  if (typeof window === "undefined") return p;
  const maxRight  = Math.max(MARGIN, window.innerWidth  - SIZE - MARGIN);
  const maxBottom = Math.max(MARGIN, window.innerHeight - SIZE - MARGIN);
  return {
    bottom: Math.min(Math.max(MARGIN, p.bottom), maxBottom),
    right:  Math.min(Math.max(MARGIN, p.right),  maxRight),
  };
}

export default function KiraFloat({
  emotion      = "neutral",
  hasInsight   = false,
  unreadCount  = 0,
  isTyping     = false,
  onClick,
  isModalOpen  = false,
}) {
  const [pos, setPos]       = useState(() => loadPos() || DEFAULT_POS);
  const [drag, setDrag]     = useState(false);
  const [hover, setHover]   = useState(false);
  const dragRef             = useRef({ moved: 0, startX: 0, startY: 0, ox: 0, oy: 0 });

  // Re-clamp on window resize
  useEffect(() => {
    const onResize = () => setPos(p => clamp(p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onMouseDown = (e) => {
    e.preventDefault();
    // Capture the bubble's current top-left in viewport coords so drag math
    // can stay in pixel space; convert back to bottom/right on commit.
    const bubbleLeft = window.innerWidth  - SIZE - pos.right;
    const bubbleTop  = window.innerHeight - SIZE - pos.bottom;
    dragRef.current = {
      moved:  0,
      startX: e.clientX,
      startY: e.clientY,
      ox:     e.clientX - bubbleLeft,
      oy:     e.clientY - bubbleTop,
    };
    setDrag(true);

    const onMove = (ev) => {
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      dragRef.current.moved = Math.max(dragRef.current.moved, Math.hypot(dx, dy));
      const newLeft = ev.clientX - dragRef.current.ox;
      const newTop  = ev.clientY - dragRef.current.oy;
      setPos(clamp({
        right:  window.innerWidth  - SIZE - newLeft,
        bottom: window.innerHeight - SIZE - newTop,
      }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
      setDrag(false);

      const wasClick = dragRef.current.moved < DRAG_THRESHOLD;
      if (wasClick) {
        onClick?.();
      } else {
        // persist new position only on a real drag
        try { localStorage.setItem(STORE, JSON.stringify(pos)); } catch {}
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
  };

  // Keep localStorage in sync after drag-end re-renders
  useEffect(() => {
    if (!drag) {
      try { localStorage.setItem(STORE, JSON.stringify(pos)); } catch {}
    }
  }, [pos, drag]);

  const wrapperStyle = {
    position:        "fixed",
    bottom:          pos.bottom,
    right:           pos.right,
    width:           SIZE,
    height:          SIZE,
    zIndex:          9999,
    cursor:          drag ? "grabbing" : "pointer",
    borderRadius:    16,
    background:      "#0d0d0d",
    border:          "1.5px solid #00ff99",
    padding:         0,
    boxShadow:       hover && !drag
      ? "0 0 18px rgba(0,255,153,0.4), 0 4px 16px rgba(0,0,0,0.7)"
      : "0 0 10px rgba(0,255,153,0.2), 0 4px 16px rgba(0,0,0,0.7)",
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
    userSelect:      "none",
    transition:      drag ? "none" : "all 0.2s ease",
  };

  return (
    <>
      <div
        onMouseDown={onMouseDown}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={wrapperStyle}
        className={hasInsight && !isModalOpen ? "kira-float-pulse" : ""}
        title="Chat with K.I.R.A"
        role="button"
        aria-label="Open K.I.R.A chat"
      >
        {/* avatar — Friendly Bot, sized natively */}
        <KiraAvatar emotion={emotion} size={54} isTyping={isTyping} />

        {/* unread badge */}
        {unreadCount > 0 && !isModalOpen && (
          <span style={{
            position:    "absolute",
            top:         -4,
            right:       -4,
            minWidth:    18,
            height:      18,
            padding:     "0 5px",
            borderRadius: 9,
            background:  "#ff4444",
            color:       "#fff",
            fontSize:    11,
            fontWeight:  700,
            display:     "grid",
            placeItems:  "center",
            border:      "2px solid #050505",
            boxShadow:   "0 0 8px rgba(255,68,68,0.10)",
          }}>
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}

        {/* close-indicator dot when modal open */}
        {isModalOpen && (
          <span style={{
            position:    "absolute",
            top:         -3,
            right:       -3,
            width:       14,
            height:      14,
            borderRadius:"50%",
            background:  NEON,
            border:      "2px solid #050505",
            boxShadow:   `0 0 6px ${NEON}`,
          }}/>
        )}

        {/* tooltip */}
        {hover && !drag && !isModalOpen && (
          <span style={{
            position:    "absolute",
            bottom:      SIZE + 10,
            left:        "50%",
            transform:   "translateX(-50%)",
            background:  "#111111",
            color:       "rgba(255,255,255,0.92)",
            border:      "1px solid rgba(255,255,255,0.08)",
            padding:     "6px 10px",
            borderRadius: 8,
            fontSize:    12,
            fontWeight:  500,
            whiteSpace:  "nowrap",
            pointerEvents: "none",
            boxShadow:   "0 4px 12px rgba(0,0,0,0.5)",
          }}>
            Chat with K.I.R.A
          </span>
        )}
      </div>

      <style>{`
        @keyframes kiraPulse {
          0%   { border-color: rgba(0,255,153,0.7); box-shadow: 0 0 10px rgba(0,255,153,0.2),  0 4px 16px rgba(0,0,0,0.7); }
          50%  { border-color: rgba(0,255,153,1.0); box-shadow: 0 0 20px rgba(0,255,153,0.55), 0 4px 16px rgba(0,0,0,0.7); }
          100% { border-color: rgba(0,255,153,0.7); box-shadow: 0 0 10px rgba(0,255,153,0.2),  0 4px 16px rgba(0,0,0,0.7); }
        }
        .kira-float-pulse { animation: kiraPulse 2s ease-in-out infinite; }
      `}</style>
    </>
  );
}
